import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {_isDesktop, _truncateDomainName} from "../../common/helpers/HelperFunc";

import styles from './ComponentSearchedDomainRow.scss';
import LoadingDomain from "../../common/primitives/LoadingDomain";
import CheckBox from "../../common/primitives/CheckBox";
import Label from "../../common/primitives/Label";
import Price from "../../common/primitives/Price";
import Button from "../../common/primitives/Button";
import buttons from "../../common/primitives/Button.scss";
import helpers from "../../common/scss/helpers/_helpers.scss";
import {ContextServicePrice} from "../../context/ContextServicePriceProvider";
import ComponentPrimaryDomainResult from "../ComponentPrimaryDomainResult/ComponentPrimaryDomainResult";
import {ContextCart} from "../../context/ContextCartProvider";
import DomainLabelNew from "../../common/primitives/DomainLabelNew";
import {__t} from '../../common/lang/text-resource';

/**
 * Показва тагчетата за дадени тлд-та
 *
 * @param {DomainData} domainData
 * @param {boolean} isPricePremium
 * @returns {*}
 */
function getTags(domainData, isPricePremium, promoCode) {
	// https://jira.superhosting.bg/browse/MARKETING-83
	if (promoCode === '822D5M48Q' && domainData.domain_tld === 'com') {
		return <div>
			<DomainLabelNew
			  textColor={`#fff`}
			  text={'подарък*'}
			  backgroundColor={`#ff0000`}
			  inSearchResult={true}/>
		</div>
	}
	const {has_tags, domain_suggested, is_free, tags, labels} = domainData;
	if (has_tags && domain_suggested && is_free) {
		if (tags['premium'] || isPricePremium) {
			return (
			  <div>
				  <DomainLabelNew
					textColor={`#fff`}
					text={'premium'}
					backgroundColor={`#2e63a9`}
					inSearchResult={true}/>
			  </div>
			)
		}

		let domainLabels = [];
		if (labels.length > 0) {
			/** @type TLDLabelData label La*/
			labels.map((label, key) => {
				domainLabels.push(<DomainLabelNew
				  key={key}
				  textColor={`#${label.text_color}`}
				  text={label.name}
				  backgroundColor={`#${label.background_color}`}
				  inSearchResult={true}/>)
			});
		}
		if (domainLabels.length > 0) {
			return <div>{domainLabels}</div>
		}
	}
}


/**
 * Показва допълнителното съобщение за БГ домейн
 *
 * @param {function} eventRaise
 * @param {boolean} hasAdditionalMessage използва се за стилове
 * @returns {*}
 */
function getBGDomainAdditionalRow(eventRaise, hasAdditionalMessage) {
	return (
	  <td className={styles.messageRow} colSpan={3}
		  data-layout={`${hasAdditionalMessage === true ? 'padding-bottom--xs' : ''} text-left`}>
		  <Button
			name={'bg_dom_info'}
			value={__t('Изисква заявка или пълномощно')}
			className={`${buttons.buttonLink} ${buttons.action} ${helpers.f10}`}
			onClick={() => eventRaise('ShowPopup', {popupName: 'bgDomainPopup'})}/>
	  </td>
	)
}

const ComponentSearchedDomainRow = React.memo(
  /**
   *
   * @param {{
   *     domainData: DomainData
   * }}props
   * @returns {*}
   */
  (props) => {

	  const {
		  domainData, messages, continueAction, submitSearchAnother, eventRaise, promoCode
	  } = props;

	  let {
		  id,
		  domain_full,
		  domain_tld,
		  is_cyrillic,
		  showBGDomainSavedMessage,
		  showBGDomainNotAllowedMessage,
		  isPremium,
		  is_free,
		  is_checked,
		  domain_suggested,
		  bg_domain_similar,
	  } = domainData;


	  /** @type ContextServicePriceProvider */
	  const servicePrice = useContext(ContextServicePrice);

	  /** @type ContextCartProvider */
	  const cartContext = useContext(ContextCart);

	  /** @type {Object.<PriceData>} */
	  let arrPrices = servicePrice.getForId(id)[12];

	  const isPricePremium = arrPrices.isPremium || isPremium;

	  if (isPremium && domainData.pricePremium) {
		  arrPrices = domainData.pricePremium;
	  }

	  /** проверяваме дали домейна е в количката */
	  const domainLineData = cartContext.getNewDomainInCart(domain_full);
	  const inCart = domainLineData.length > 0;

	  /** За BG домейните проверяваме и двойката */
	  if (bg_domain_similar !== '') {
		  const similarDomainLineData = cartContext.getNewDomainInCart(bg_domain_similar);
		  showBGDomainSavedMessage = domainLineData.length === 0 && similarDomainLineData.length === 1;
	  }
	  /**
	   * Добавя или Премахва услуга от количката
	   *
	   * @param {boolean} checked
	   */
	  const addRemoveDomainFromCart = (checked) => {
		  eventRaise('ToggleCartDisabledState', {disable: true});

		  if (checked) {
			  /** Добавяне на услуга в количката */
			  if (domainLineData.length === 0) {
				  cartContext.addDomainToCart(domain_full, arrPrices.id, () => {
					  eventRaise('ServiceAddedToCart');
				  });
			  } else {
				  eventRaise('ToggleCartDisabledState', {disable: false});
			  }
		  } else {
			  /** Премахване на услуга от количката */
			  if (domainLineData.length > 0) {
				  cartContext.removeFromCart(domainLineData[0].id);
			  } else {
				  eventRaise('ToggleCartDisabledState', {disable: false});
			  }
		  }
	  };


	  let domain_full_formatted;
	  if (is_checked || _isDesktop()) {
		  domain_full_formatted = _truncateDomainName(domain_full, domain_tld, 24);
	  } else {
		  domain_full_formatted = domain_full.substring(0, 16);
		  if (domain_full_formatted !== domain_full) {
			  domain_full_formatted = domain_full_formatted + '…';
		  }
	  }
	  const hasBgDomainAdditionalMessage = (showBGDomainSavedMessage && is_free) || (showBGDomainNotAllowedMessage && is_checked);

	  return (
		<React.Fragment key={domainData.id}>
			{/* Докато домейна не се провери, показваме анимация */}
			{
			  !is_checked &&
			  <tr className={`${styles.row} ${styles.loadingRow}`}>
				  <td className={styles.firstCol} data-layout="padding-left--15">&nbsp;</td>
				  <td className={styles.domainName} colSpan={2} data-layout="text-left padding-left--smm">
					  <Label
						htmlFor={is_free ? id : undefined}
						text={domain_full_formatted} title={domain_full}/>
					  <div className={styles.loadingContainer}>
						  <LoadingDomain/>
					  </div>
				  </td>
			  </tr>
			}

			{/*
				 *	Ако провереният домейн не е алтернативен, значи клиента е търсил без tld.
				 *  Показваме този домейн със стиловете на основния домейн.
				 */}
			{
			  is_checked && !domain_suggested &&
			  <tr className={styles.primaryDomainRow}>
				  <td colSpan={3} data-test="primary-domain-row">
					  <ComponentPrimaryDomainResult
						domainData={domainData}
						searchedWithTLD={false}
						messages={messages}
						continueAction={continueAction}
						submitSearchAnother={submitSearchAnother}
						hideButtons={false}
						hideMessage={false}
						eventRaise={eventRaise}/>
				  </td>
			  </tr>
			}


			{/* От тук почва редът с алтернативния домейн. Показват се само свободни домейни */}
			{
			  (is_checked && domain_suggested === 1) &&
			  <tr
				className={`${styles.row} ${(is_free) ? styles.isFree : styles.isDisabled} ${inCart ? styles.rowInCart : ''}`}
				data-layout="text-left"
				data-test={`domain_name_suggested_${domain_full}_id_${id}`}>
				  <td colSpan={3}>
					  <table>
						  <tbody>
						  <tr>
							  {/* Чекбокс със лейбъл */}
							  <td className={styles.firstCol} data-layout="padding-left--15">
								  {
									is_free &&
									<CheckBox
									  name={id}
									  checked={inCart}
									  customOnchange={addRemoveDomainFromCart}/>
								  }
							  </td>
							  <td className={styles.domainName}
								  data-layout={` padding-left--smm ${(domain_tld === 'bg' || domain_tld === 'бг') && is_free ? 'padding-bottom--none' : ''}`}>
								  <Label
									htmlFor={is_free ? id : undefined}
									text={domain_full_formatted} title={domain_full}/>
							  </td>

							  {/* Цени */}
							  <td className={styles.priceAndTags}>
								  {
									is_free &&
									<table>
										<tbody>
										<tr>
											{/* Тагове */}
											<td
											  className={styles.tagsContainer}>{getTags(domainData, isPricePremium, promoCode)}</td>
											<td className={styles.priceCol}>
												{
													(is_free && (!isPremium && !isPricePremium) && parseFloat(arrPrices['priceNew']) < parseFloat(arrPrices['basePriceNew'])) ?
													  <Price
														value={arrPrices['basePriceNew']}
														valueClass={styles.oldPrice}/>
													  : ''
												}
												{SHVO.showEuroPrices && <>
												<br/>
												{
													(is_free && (!isPremium && !isPricePremium) && parseFloat(arrPrices['priceNew']) < parseFloat(arrPrices['basePriceNew'])) ?
													  <Price
														value={arrPrices['basePriceNewEUR']}
														valueClass={styles.oldPrice}/>
													  : ''
												}
												</>}
											</td>
											{
												(is_free) ?
												  <td className={styles.priceCol}>
													  <Price
														currency={arrPrices['currency']}
														value={arrPrices['priceNew']}
														valueClass={styles.priceValue}
														currencyClass={styles.priceCurrency}/>
													  {SHVO.showEuroPrices && <>
														  <br/>
														  <Price
															currency={arrPrices['currencyEUR']}
															value={arrPrices['priceNewEUR']}
															valueClass={styles.priceValue}
															currencyClass={styles.priceCurrency}/>
													  </>}
												  </td>
												  : <td/>
											}
											<td className={styles.lastCol}>&nbsp;</td>
										</tr>
										</tbody>
									</table>
								  }
							  </td>
						  </tr>
						  {/* Съобщение за BG домейни */}
						  {
							  ((domain_tld === 'bg' || domain_tld === 'бг') && is_free && is_checked) ?
								<tr>
									{getBGDomainAdditionalRow(eventRaise, hasBgDomainAdditionalMessage)}
								</tr> : null
						  }


						  {/* Съобщение за BG домейни */}
						  {
							  hasBgDomainAdditionalMessage ?
								<tr>
									<td className={styles.messageRow} colSpan={3}
										data-layout="text-left">
										<Button
										  name={`bg_dom_info_saved_${domain_tld}`}
										  value={showBGDomainNotAllowedMessage ? __t('Неразрешена регистрация') : __t('Домейнът се запазва за регистрация от вас')}
										  className={`${buttons.buttonLink} ${buttons.action} ${helpers.f10}`}
										  onClick={() => eventRaise('ShowPopup', {
											  popupName: showBGDomainNotAllowedMessage ? 'bgDomainPopupNotAllowed' : 'bgDomainPopupSaved',
											  replaceField: '%!domain_name!%',
											  replaceValue: domain_full
										  })}/>
									</td>
								</tr>
								: null
						  }
						  {/* Съобщение за премиум домейн */}
						  {
							  (isPremium || isPricePremium) && is_checked ?
								<tr>
									<td className={styles.messageRow} colSpan={3}
										data-layout="text-left">
										<Button
										  name={'premium_dom_info'}
										  value={__t('Регистрация на специални (premium) домейни')}
										  className={`${buttons.buttonLink} ${buttons.action} ${helpers.f10}`}
										  onClick={() => eventRaise('ShowPopup', {popupName: 'premiumDomainPopup'})}/>
									</td>
								</tr> : null
						  }
						  {/* Ако домейна е в количката, показваме съобщение */}

						  </tbody>
					  </table>
				  </td>

			  </tr>
			}
			{
			  (inCart && is_checked && domain_suggested === 1) === true &&
			  <tr>
				  <td colSpan={3}>
					  <div className={styles.yellowBlock}>
						  {__t('Домейнът е добавен в поръчката!')}
					  </div>
				  </td>
			  </tr>
			}
		</React.Fragment>
	  )
  }
);

ComponentSearchedDomainRow.propTypes = {
	domainData: PropTypes.object.isRequired,
	messages: PropTypes.objectOf(PropTypes.string),
};

export default ComponentSearchedDomainRow;